import { useEffect, useMemo, useState } from 'react'
import { useBackground } from '../utils/hooks'
import { CustomerButton } from './CustomerButton'
import { CLink, LinkComponentType } from './CLink'
import {
  BackgroundColor,
  DynamicContentType,
  VideoHeroData,
} from '@bm-js/atlascloud-shared'
import { uiEnv } from '../env'
import { WavySvg } from './WavySvg'

type Props = {
  LinkComponent: LinkComponentType
  content: DynamicContentType<VideoHeroData>
  websiteBackground: BackgroundColor | undefined
}

const overlayBase =
  "after:absolute after:content-[''] after:h-full after:inset-0 after:w-full"

export const VideoHero = ({
  content,
  LinkComponent,
  websiteBackground,
}: Props) => {
  const {
    file,
    headerText,
    paragraphText,
    ctaText,
    ctaLink,
    overlayPercentage,
  } = content.data

  const [videoIsReady, setVideoIsReady] = useState(false)

  const src = `${uiEnv.s3FilesBase}/uploads/${file?.uuid}`
  const svgFill = useBackground(websiteBackground, true)
  const background = useBackground(websiteBackground)

  const overlayClass = useMemo(() => {
    const overlayOptions: Record<number, string> = {
      0: '',
      10: 'after:bg-black/10',
      20: 'after:bg-black/20',
      30: 'after:bg-black/30',
      40: 'after:bg-black/40',
      50: 'after:bg-black/50',
      60: 'after:bg-black/60',
      70: 'after:bg-black/70',
      80: 'after:bg-black/80',
      90: 'after:bg-black/90',
      100: 'after:bg-black/100',
    }

    const applied = overlayOptions[overlayPercentage || 0] || ''

    return `${overlayBase} ${applied}`
  }, [overlayPercentage])

  const onVideoLoad = () => {
    setVideoIsReady(true)
  }

  // Fallback to load video in case load events don't fire
  useEffect(() => {
    const timeout = setTimeout(() => {
      setVideoIsReady(true)
    }, 1000)

    return () => {
      clearTimeout(timeout)
    }
  }, [])

  return (
    <>
      <div
        className={`relative h-[calc(100vh+80px)] @container ${background} ${overlayClass} ${
          videoIsReady ? 'after:opacity-100' : 'after:opacity-0'
        } transition-opacity duration-500`}
      >
        <video
          onCanPlay={onVideoLoad}
          onLoadedData={onVideoLoad}
          src={src}
          playsInline
          muted
          autoPlay
          controls={false}
          loop
          className={`h-full w-full object-cover object-center ${
            videoIsReady ? 'opacity-100' : 'opacity-0'
          } transition-opacity duration-500`}
        />
        <div className='absolute inset-0 z-10 flex items-center justify-center pb-[80px]'>
          <div className='px-4 text-center text-white'>
            <h1 className=' mx-auto mb-6 max-w-[340px] text-4xl font-medium leading-[100%] @2xl:max-w-[780px] @2xl:text-[5rem]'>
              {headerText}
            </h1>
            <p className='mx-auto mb-8 max-w-[350px] text-base @2xl:max-w-[570px] @2xl:text-xl'>
              {paragraphText}
            </p>
            {!!ctaLink && !!ctaText && (
              <CLink LinkComponent={LinkComponent} href={ctaLink}>
                <CustomerButton btnType='filled' btnColor='light'>
                  {ctaText}
                </CustomerButton>
              </CLink>
            )}
          </div>
        </div>
        <WavySvg
          className={`absolute -bottom-1 left-0 right-0 z-10 ${svgFill}`}
        />
      </div>
    </>
  )
}
